import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';

interface SourceRow {
  sourceId: number;
  sourceName: string;
  shopId: number | null;
  orderCount: number;
  cashCount: number;
  cashTotal: number;
  cardCount: number;
  cardTotal: number;
  grandTotal: number;
  date: string;
}
interface DayBlock {
  date: string;
  isToday: boolean;
  sources: SourceRow[];
}

interface Props {
  shopId: number;
}

const useStyles = makeStyles((theme) => ({
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  todayCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '2px solid #2e7d32',
    background: '#f1f8e9',
  },
  bigNum: { fontWeight: 700, fontSize: '1.6rem' },
  cash: { color: '#e65100', fontWeight: 700 },
  card: { color: '#1565c0', fontWeight: 700 },
  muted: { color: theme.palette.text.secondary },
}));

const eur = (n: number) =>
  `€${(n || 0).toLocaleString('en-MT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const DailySalesView: React.FC<Props> = ({ shopId }) => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState<DayBlock[]>([]);

  const load = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.getDailySales(30)
      .then((r) => setDays(r.days || []))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load, shopId]);

  // Keep only sources belonging to the selected shop.
  const forShop = (rows: SourceRow[]) =>
    rows.filter((s) => s.shopId === null || s.shopId === shopId);

  if (loading) {
    return (
      <Box style={{ padding: 32, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const today = days.find((d) => d.isToday);
  const history = days.filter((d) => !d.isToday);

  return (
    <Box>
      <Paper className={classes.todayCard}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Today · {today?.date}
        </Typography>
        {forShop(today?.sources || []).length === 0 ? (
          <Typography className={classes.muted} style={{ marginTop: 8 }}>
            No sales yet today.
          </Typography>
        ) : (
          forShop(today?.sources || []).map((s) => (
            <Box
              key={s.sourceId}
              style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}
            >
              <Chip label={s.sourceName} color="primary" />
              <span className={classes.bigNum}>{eur(s.grandTotal)}</span>
              <span className={classes.muted}>{s.orderCount} orders</span>
              <span className={classes.card}>
                CARD {eur(s.cardTotal)} ({s.cardCount})
              </span>
              <span className={classes.cash}>
                CASH {eur(s.cashTotal)} ({s.cashCount})
              </span>
            </Box>
          ))
        )}
        <Typography variant="caption" className={classes.muted} style={{ display: 'block', marginTop: 12 }}>
          CASH = collected by the driver in person (reconcile physically). CARD = paid out by the platform.
        </Typography>
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
          Previous days
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell align="right">Orders</TableCell>
              <TableCell align="right">Card</TableCell>
              <TableCell align="right">Cash</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className={classes.muted}>
                  No history yet — daily totals are saved each night.
                </TableCell>
              </TableRow>
            ) : (
              history.flatMap((d) =>
                forShop(d.sources).map((s) => (
                  <TableRow key={`${d.date}-${s.sourceId}`}>
                    <TableCell>{d.date}</TableCell>
                    <TableCell>{s.sourceName}</TableCell>
                    <TableCell align="right">{s.orderCount}</TableCell>
                    <TableCell align="right" className={classes.card}>
                      {eur(s.cardTotal)} ({s.cardCount})
                    </TableCell>
                    <TableCell align="right" className={classes.cash}>
                      {eur(s.cashTotal)} ({s.cashCount})
                    </TableCell>
                    <TableCell align="right" style={{ fontWeight: 700 }}>
                      {eur(s.grandTotal)}
                    </TableCell>
                  </TableRow>
                )),
              )
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
